import { Breadcrumb, Button, Col, Dropdown, Menu, notification, Pagination, Row, Select, Space, Statistic, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../redux/Hooks";
import { PropertyTypes } from "../../common/types/types";
import AddProductModal from "./components/AddProductModal";
import { ReactComponent as ListAction } from '../../../assets/images/list_action.svg';
import { ReactComponent as ListEdit } from '../../../assets/images/list_edit.svg';
import { ReactComponent as ListDelete } from '../../../assets/images/list_delete.svg';
import ConfirmModal from "../../common/components/ConfirmModal";
import { useCreateContractMutation } from "../../../redux/api/ContractApi";
import { usePrompt } from "../../prompts";
import { Product } from "../types";

const { Option } = Select;

interface AuctionRegisterTable {
    key: number,
    productName: string,
    productType: string,
    productCategoryCode: string,
    startPrice: number,
    description: string,
    statuteId: number,
}

const AuctionRegisterList = () => {
    const [tableData, setTableData] = useState<AuctionRegisterTable[]>([]);
    const [addVisible, setAddVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [cancelVisible, setCancelVisible] = useState(false);
    const [editId, setEditId] = useState(0);
    const [statuteId, setStatuteId] = useState(0);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [deleteIndex, setDeleteIndex] = useState(-1);
    const [noCheck, setNoCheck] = useState(false);

    const [createContract, { isLoading }] = useCreateContractMutation();
    const navigate = useNavigate();
    const divRef = useRef<HTMLDivElement>(null);
    const userState = useAppSelector(state => state.login);

    usePrompt(tableData.length > 0 && !noCheck);

    useEffect(() => {
        scrollToStart();
    }, [])

    const scrollToStart = () => {
        if (divRef.current) {
            divRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }

    const toggleAddModal = () => {
        if (addVisible) {
            setEditId(0);
        }
        setAddVisible(!addVisible);
    }

    const toggleConfirm = () => {
        setConfirmVisible(!confirmVisible);
    }

    const toggleCancel = () => {
        setCancelVisible(!cancelVisible);
    }

    const openRemoveProductModal = (index: number) => {
        setDeleteIndex(index);
        toggleConfirm();
    }

    const setProduct = (product: Product) => {
        let newTableData: AuctionRegisterTable[] = [...tableData];
        let itemIndex = newTableData.findIndex(item => item.key === product.productId);

        let productType = PropertyTypes.find(item => item.id === product.productType);
        let categoryNames = product.categories.map(item => item.name);
        if (itemIndex !== -1) {
            newTableData[itemIndex] = {
                key: product.productId,
                description: product.description || "",
                productName: product.productName,
                startPrice: product.startPrice || 0,
                productType: productType?.name || "",
                productCategoryCode: categoryNames.join(", "),
                statuteId: product.statute?.statuteId || 0,
            }
        } else {
            let newTableRow: AuctionRegisterTable = {
                key: product.productId,
                description: product.description || "",
                productName: product.productName,
                startPrice: product.startPrice || 0,
                productType: productType?.name || "",
                productCategoryCode: categoryNames.join(", "),
                statuteId: product.statute?.statuteId || 0,
            };
            newTableData.push(newTableRow);
        }

        setTableData(newTableData);
    }

    const openEditModal = (id: number, statuteId: number) => {
        setEditId(id);
        setStatuteId(statuteId);
        toggleAddModal();
    }

    const removeProduct = () => {
        if (deleteIndex >= 0) {
            let newTableData = [...tableData];
            newTableData.splice(deleteIndex, 1);
            setTableData(newTableData);
            setDeleteIndex(-1);
            toggleConfirm();
        }
    }

    const auctionRegisterTableColumns: ColumnsType<AuctionRegisterTable> = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 70,
            align: 'center',
            render: (value, record, index) => {
                return (<span
                    style={{ border: '1px solid black', padding: '7px 13px', borderRadius: '20px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    key={index}>
                    {(page - 1) * size + index + 1}
                </span>)
            }
        },
        {
            title: 'TÊN TÀI SẢN',
            dataIndex: 'productName',
            key: 'productName',
            ellipsis: true,
        },
        {
            title: 'LOẠI TÀI SẢN',
            dataIndex: 'productType',
            key: 'productType',
            ellipsis: true,
        },
        {
            title: 'NHÓM TÀI SẢN',
            dataIndex: 'productCategoryCode',
            key: 'productCategoryCode',
            ellipsis: true,
        },
        {
            title: 'GIÁ KHỞI ĐIỂM',
            dataIndex: 'startPrice',
            key: 'startPrice',
            render: (value) => {
                return (
                    <Statistic
                        value={value}
                        groupSeparator="."
                        decimalSeparator=","
                        precision={0}
                        valueStyle={{ fontFamily: 'Quicksand', fontSize: 14, fontWeight: 'bold' }} />
                )
            }
        },
        {
            title: 'MÔ TẢ',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            width: 70,
            key: 'action',
            render: (value, record, index) => {
                return (
                    // <div className="cursor-pointer">
                    //     <ListAction />
                    // </div>
                    <Dropdown overlay={() => renderActionButtons(record, index)} className="cursor-pointer" overlayClassName="table-dropdown">
                        <ListAction />
                    </Dropdown>
                )
            }
        }
    ]

    const renderActionButtons = (record: AuctionRegisterTable, index: number) => {
        let menu = (
            <Menu className="custom-list-dropdown">
                <Menu.Item key="edit" onClick={() => openEditModal(record.key, record.statuteId)}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ListEdit style={{ marginRight: 10 }} />Chỉnh sửa
                    </div>
                </Menu.Item>
                <Menu.Item onClick={() => openRemoveProductModal(index)} key="delete">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ListDelete style={{ marginRight: 10 }} />Xoá
                    </div>
                </Menu.Item>
            </Menu>
        )
        return menu
    }

    const onFinish = () => {
        if (!tableData.length) {
            notification.error({
                message: "Cần thêm ít nhất 1 tài sản!"
            })
        } else {
            let productIds: number[] = [];
            productIds = tableData.map(item => item.key);
            setNoCheck(true);
            // if (!param.contractId) {
            createContract({
                productIds,
                sellerId: userState.user.userId,
                status: 0,
                contractCode: ""
            }).unwrap()
                .then(() => {
                    // navigate("/auction/register", { replace: true, state: { from: location } })
                    navigate("/");
                    notification.success({
                        message: "Thêm mới đăng ký bán tài sản thành công"
                    })
                })
                .catch((err: any) => {
                    console.log(err);
                    notification.error({
                        message: err.data?.message || "Đã có lỗi xảy ra, xin vui lòng thử lại!"
                    })
                })
            // } else if (param.contractId && parseInt(param.contractId)) {
            //     editContract({
            //         contractId: parseInt(param.contractId),
            //         productIds,
            //         sellerId,
            //     }).unwrap()
            //         .then(() => {
            //             navigate(-1);
            //             notification.success({
            //                 message: "Chỉnh sửa đăng ký bán tài sản thành công"
            //             })
            //         })
            //         .catch((err: any) => {
            //             console.log(err);
            //             notification.error({
            //                 message: err.data?.message || "Đã có lỗi xảy ra, xin vui lòng thử lại!"
            //             })
            //         })
            // }

        }
    }

    const onPageSizeChange = (currentSize: number) => {
        setSize(currentSize);
        setPage(1)
    }

    const onPageChange = (currentPage: number) => {
        setPage(currentPage);
    }

    return (
        <div
            ref={divRef}
            style={{
                // padding: '40px calc((100vw - 1440px) / 2) 40px',
                background: "#ececec",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                width: '100%',
                paddingTop: 40,
                paddingBottom: 40
            }}
        >
            <Space style={{ display: 'flex', alignItems: 'center' }} className="responsive-padding-1440">

                <Breadcrumb separator="  " className="custom-breadcrumb">
                    <Breadcrumb.Item>
                        <Link to="/">
                            Trang chủ
                        </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        Đăng ký bán tài sản
                    </Breadcrumb.Item>
                </Breadcrumb>

                <div className="responsive-title">Đăng ký bán tài sản</div>
            </Space>
            <Row className="responsive-padding-1440" style={{ marginTop: 45, width: '100%' }} justify="center">

                <Col span={24}>
                    <Row align="middle" justify="space-between" className="font-bold font-size-16">
                        <span>Thông tin tài sản đấu giá</span>
                        <Button className="button-light-yellow font-size-12" onClick={toggleAddModal}>THÊM TÀI SẢN</Button>
                    </Row>

                    <div className='custom-table-wrapper'>
                        <Table
                            columns={auctionRegisterTableColumns}
                            dataSource={tableData.slice((page - 1) * size, (page - 1) * size + size)}
                            pagination={false}
                        />
                    </div>

                    <Row justify="end" align="middle" style={{ marginTop: 35 }}>
                        <Select className="left-arrow-select" defaultValue={10} value={size} onChange={onPageSizeChange} dropdownStyle={{ borderRadius: 15 }}>
                            <Option value={10}>10</Option>
                            <Option value={20}>20</Option>
                            <Option value={50}>50</Option>
                            <Option value={100}>100</Option>
                        </Select>
                        <div style={{ marginRight: 20, marginLeft: 8, fontSize: 14 }}>Bản ghi</div>
                        <Pagination
                            defaultCurrent={1}
                            total={tableData.length || 0}
                            defaultPageSize={10}
                            pageSize={size}
                            className="custom-pagination"
                            current={page}
                            onChange={onPageChange}
                            showSizeChanger={false}
                        />
                    </Row>
                </Col>
            </Row>
            <Row className="responsive-padding-1440" justify="center" style={{ marginTop: 45 }}>
                <Col span={16} style={{ borderRadius: 15, background: 'white', padding: '28px 80px' }}>
                    <Row>
                        <Col span={18} style={{ fontSize: 16 }}>
                            <Row gutter={15}>
                                <Col span={8} style={{ textAlign: 'right' }}>
                                    Người có tài sản
                                </Col>
                                <Col span={16} className="font-bold text-ellipsis">
                                    {userState.user?.fullName}
                                </Col>
                            </Row>
                            <Row gutter={15}>
                                <Col span={8} style={{ textAlign: 'right' }}>
                                    Địa chỉ
                                </Col>
                                <Col span={16} className="font-bold text-ellipsis">
                                    {userState.user.address || "-"}
                                </Col>
                            </Row>
                            <Row gutter={15}>
                                <Col span={8} style={{ textAlign: 'right' }}>
                                    Số điện thoại
                                </Col>
                                <Col span={16} className="font-bold text-ellipsis">
                                    {userState.user.phoneNumber || "-"}
                                </Col>
                            </Row>
                            <Row gutter={15}>
                                <Col span={8} style={{ textAlign: 'right' }}>
                                    Email
                                </Col>
                                <Col span={16} className="font-bold text-ellipsis">
                                    {userState.user.email || "-"}
                                </Col>
                            </Row>
                        </Col>
                        <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Button
                                className={!tableData.length ? "button-gray" : "button-light-yellow"}
                                style={{ height: 50, minWidth: 180, fontSize: 12 }}
                                onClick={onFinish}
                                disabled={!tableData.length || isLoading} loading={isLoading}>
                                GỬI ĐĂNG KÝ
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {addVisible ?
                <AddProductModal
                    visible={addVisible}
                    toggleModal={toggleAddModal}
                    editId={editId}
                    statuteId={statuteId}
                    sellerId={userState.user.userId || 0}
                    setEditId={setEditId}
                    setStatuteId={setStatuteId}
                    setProduct={setProduct}
                    toggleCancel={toggleCancel} />
                : <></>}
            {confirmVisible ?
                <ConfirmModal
                    onCancel={toggleConfirm}
                    onOk={removeProduct}
                    status="error"
                    title={<div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="font-size-16">Bạn có chắc chắn muốn xóa tài sản này hay không?</div>
                    </div>}
                    visible={confirmVisible}
                />
                : <></>}
            {cancelVisible ?
                <ConfirmModal
                    visible={cancelVisible}
                    status="error"
                    onOk={() => {
                        toggleAddModal();
                        toggleCancel()
                    }}
                    onCancel={toggleCancel}
                    title={<div style={{ textAlign: 'center' }}>
                        <div className="font-size-16">Bạn có chắc chắn muốn thoát và không lưu dữ liệu vừa nhập</div>
                    </div>}
                /> : <></>}
        </div>
    )
}

export default AuctionRegisterList;